import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { CardDetails } from 'src/app/interfaces/card-details';
import { Session } from 'src/app/interfaces/session-data';
import { CardService } from 'src/app/services/card/card.service';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';

interface CardTypeInfo {
  type: string;
  logo: string;
  alt: string;
}

@Component({
  selector: 'app-multi-card-option-version-two',
  templateUrl: './multi-card-option.component.html',
  styleUrls: ['./multi-card-option.component.scss']
})
export class MultiCardOptionComponent implements OnInit {

  public cards:SelectCard[];
  public loading:boolean;

  private session:Session;

  // Card type mappings
  private cardTypeMap: { [key: string]: CardTypeInfo } = {
    // Standard formats
    'visa': {
      type: 'Visa',
      logo: 'assets/icons/Icons/card-types/visa.svg',
      alt: 'Visa'
    },
    'mastercard': {
      type: 'Mastercard',
      logo: 'assets/icons/Icons/card-types/mastercard.svg',
      alt: 'Mastercard'
    },
    'american express': {
      type: 'American Express',
      logo: 'assets/icons/Icons/card-types/amex.svg',
      alt: 'American Express'
    },
    'amex': {
      type: 'American Express',
      logo: 'assets/icons/Icons/card-types/amex.svg',
      alt: 'American Express'
    },
    'diners': {
      type: 'Diners Club',
      logo: 'assets/icons/Icons/card-types/diners.svg',
      alt: 'Diners Club'
    },
    'diners club': {
      type: 'Diners Club',
      logo: 'assets/icons/Icons/card-types/diners.svg',
      alt: 'Diners Club'
    },
    'maestro': {
      type: 'Maestro',
      logo: 'assets/icons/Icons/card-types/maestro.svg',
      alt: 'Maestro'
    },
    'visa-electron': {
      type: 'Visa Electron',
      logo: 'assets/icons/Icons/card-types/visa-electron.svg',
      alt: 'Visa Electron'
    },
    'visa electron': {
      type: 'Visa Electron',
      logo: 'assets/icons/Icons/card-types/visa-electron.svg',
      alt: 'Visa Electron'
    },
    // Alternative naming conventions that might come from backend
    'master': {
      type: 'Mastercard',
      logo: 'assets/icons/Icons/card-types/mastercard.svg',
      alt: 'Mastercard'
    },
    'master card': {
      type: 'Mastercard',
      logo: 'assets/icons/Icons/card-types/mastercard.svg',
      alt: 'Mastercard'
    },
    'american_express': {
      type: 'American Express',
      logo: 'assets/icons/Icons/card-types/amex.svg',
      alt: 'American Express'
    },
    'amex_card': {
      type: 'American Express',
      logo: 'assets/icons/Icons/card-types/amex.svg',
      alt: 'American Express'
    },
    'diners_club': {
      type: 'Diners Club',
      logo: 'assets/icons/Icons/card-types/diners.svg',
      alt: 'Diners Club'
    },
    // Default fallback
    'default': {
      type: 'Credit Card',
      logo: 'assets/icons/Icons/card-types/visa.svg',
      alt: 'Credit Card'
    }
  };

  constructor(
    private dialogRef:MatDialogRef<MultiCardOptionComponent>,
    private cardService:CardService,
    private snackBar:SnackbarService
  ) { 
    this.loading = false;
  }

  ngOnInit(): void {
    this.session = JSON.parse(sessionStorage.getItem("sessionData"));
    this.cards = this.session.savedCards.map((v)=>{
      return {
        ...v,
        selected:false
      }
    });
    if(this.cards.length==0){
      this.close();
    }
  }

  getColorScheme(): string {
    if (this.session && this.session.sessionDisplayInformation && this.session.sessionDisplayInformation.colorScheme) {
      return this.session.sessionDisplayInformation.colorScheme.replace(';', '');
    }
    return 'inherit';
  }

  getTextColor(): string {
    if (this.session && this.session.sessionDisplayInformation && this.session.sessionDisplayInformation.colorScheme) {
      let bgColor:string = this.session.sessionDisplayInformation.colorScheme.replace(';', '');
      let color = bgColor.charAt(0) === '#' ? bgColor.substring(1, 7) : bgColor;
      let r = parseInt(color.substring(0, 2), 16); // Red
      let g = parseInt(color.substring(2, 4), 16); // Green
      let b = parseInt(color.substring(4, 6), 16); // Blue

      // Calculate the perceptive luminance
      let luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

      // Return black for bright colors, white for dark colors
      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    }
    return 'inherit';
  }

  close(){
    this.loading=true;
    // Close dialog with 'x-close' to indicate user clicked X button
    this.dialogRef.close('x-close');
  }

  addNewCard(){
    this.loading=true;
    // Close dialog with 'add-new' to indicate user clicked Add New Card
    this.dialogRef.close('add-new');
  }

  deleteCard(card:SelectCard){
    if(!this.loading){
      this.loading = true;
      this.cardService.deleteCard(card.cardToken,sessionStorage.getItem('sessionToken')).subscribe(()=>{
        this.cards = this.cards.filter((v)=>v.cardToken!=card.cardToken);
        this.session.savedCards = this.session.savedCards.filter((v)=>v.cardToken!=card.cardToken);
        sessionStorage.setItem("sessionData",JSON.stringify(this.session));
        this.snackBar.showSnackBar("Card deleted successfully");
        
        // If no cards left, close the dialog
        if(this.cards.length === 0){
          this.handleError("No saved cards remaining");
          setTimeout(() => {
            this.dialogRef.close();
          }, 1500);
        }
      },()=>this.handleError("Failed to delete the card"),()=>this.loading=false);
    }
  }

  selectCard(card:SelectCard){
    if(!this.loading){
      // Select the clicked card and close dialog
      this.dialogRef.close(card);
    }
  }

  /**
   * Gets card logo information based on card details
   * Prioritizes backend-provided cardType, falls back to client-side detection
   */
  getCardLogo(card: CardDetails): CardTypeInfo {
    let cardType: string;
    
    // Prioritize backend-provided card type
    if (card.cardType && card.cardType.trim() !== '') {
      cardType = card.cardType.toLowerCase().trim();
    } else {
      // Fallback to client-side detection for backward compatibility
      cardType = this.getCardTypeFromNumber(card.cardNumber || card.actualCardNumber);
    }
    
    return this.cardTypeMap[cardType] || this.cardTypeMap['default'];
  }

  /**
   * Client-side card type detection (fallback method)
   * Renamed from getCardType to be more explicit about its purpose
   */
  private getCardTypeFromNumber(cardNumber: string): string {
    if (!cardNumber) return 'default';
    
    // Remove any spaces or non-numeric characters
    const cleanNumber = cardNumber.replace(/\D/g, '');
    
    // American Express: starts with 34 or 37
    if (/^3[47]/.test(cleanNumber)) {
      return 'amex';
    }
    
    // Diners Club: starts with 30[0-5] or 36 or 38
    if (/^3[0689]/.test(cleanNumber)) {
      return 'diners';
    }
    
    // Visa Electron: specific ranges (4026, 417500, 4508, 4844, 491(7-3))
    if (/^4(026|17500|508|844|91[3-7])/.test(cleanNumber)) {
      return 'visa-electron';
    }
    
    // Visa: starts with 4 (check this after Visa Electron)
    if (/^4/.test(cleanNumber)) {
      return 'visa';
    }
    
    // Mastercard: starts with 5[1-5] or 2[2-7]
    if (/^5[1-5]/.test(cleanNumber) || /^2[2-7]/.test(cleanNumber)) {
      return 'mastercard';
    }
    
    // Maestro: starts with 5[06-8] or 6304 or 67
    if (/^5[068]/.test(cleanNumber) || /^6304/.test(cleanNumber) || /^67/.test(cleanNumber)) {
      return 'maestro';
    }
    
    return 'default';
  }

  /**
   * Gets formatted card number for display (keeps last 4 digits visible)
   */
  getFormattedCardNumber(cardNumber: string): string {
    if (!cardNumber) return '';
    
    // If the card number is already masked (contains *), return as is
    if (cardNumber.includes('*')) {
      return cardNumber;
    }
    
    // Otherwise, mask all but last 4 digits
    const cleanNumber = cardNumber.replace(/\D/g, '');
    if (cleanNumber.length < 4) return cardNumber;
    
    const lastFour = cleanNumber.slice(-4);
    const maskedSection = '**** **** ****';
    return `${maskedSection} ${lastFour}`;
  }

  private handleError(msg:string){
    this.snackBar.showSnackBar(msg);
  }

}

interface SelectCard extends CardDetails{
  selected:boolean;
}
